import React, { useState, useEffect } from "react";
import {
  Typography,
  Container,
  Grid,
  TextField,
  Button,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Footer from '../Materials/Footer';
import { useAuth } from '../../context/AuthContext';
import BurgerBar from '../Materials/BurgerBar';

export default function ScheduleSpeedMeeting() {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [contact, setContact] = useState("");
  const [speedmeetingStateId, setSpeedmeetingStateId] = useState("");
  const [speedmeetingStates, setSpeedmeetingStates] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false
    }));
  };

  useEffect(() => {
    // Fetch speedmeeting states when component mounts
    const fetchSpeedmeetingStates = async () => {
      try {
        const response = await fetch('https://api.hub4food.colab4food.com/api/speedmeeting-states');
        if (!response.ok) {
          throw new Error('Failed to fetch speedmeeting states');
        }
        const data = await response.json();
        setSpeedmeetingStates(data);
      } catch (error) {
        console.error("Error fetching speedmeeting states:", error);
      }
    };

    fetchSpeedmeetingStates();
  }, []);

  const handleSubmit = async () => {
    if (!user) {
      showNotification(
        "User not logged in. Please log in to schedule a speedmeeting.",
        "warning"
      );
      return;
    }

    if (!subject || !description || !startDate || !endDate || !contact || !speedmeetingStateId) {
      showNotification(
        "Please fill in all fields.",
        "warning"
      );
      return;
    }

    // Validate that end date is after start date
    if (new Date(endDate) <= new Date(startDate)) {
      showNotification(
        "End date must be after start date",
        "warning"
      );
      return;
    }

    const formData = {
      user_id: user.userId, 
      subject,
      description,
      contact,
      date: startDate,
      end_date: endDate,
      speedmeeting_state_id: speedmeetingStateId,
    };

    console.log("A enviar:", formData);

    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/speedmeetings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Erro ao agendar speedmeeting');
      }

      const result = await response.json();
      console.log("Speedmeeting agendado com sucesso:", result);

      showNotification(
        "SpeedMeeting agendado com sucesso!",
        "success"
      );

      // Reset form after successful submission
      setSubject("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setContact("");
      setSpeedmeetingStateId("");
    } catch (error) {
      console.error("Erro ao agendar speedmeeting:", error);

      showNotification(
        "Erro ao agendar speedmeeting. Tente novamente.",
        "error"
      );
    }
  };

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <BurgerBar />

      {/* Conteúdo Principal */}
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>Schedule Speedmeeting</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Subject"
              fullWidth
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Brief description"
              fullWidth
              required
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="End Date & Time"
              type="datetime-local"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              error={endDate && startDate && new Date(endDate) <= new Date(startDate)}
              helperText={
                endDate && startDate && new Date(endDate) <= new Date(startDate) 
                  ? "End date must be after start date" 
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id="contact-method-label">Contact</InputLabel>
              <Select
                labelId="contact-method-label"
                id="contact-method"
                value={contact}
                label="Contact"
                onChange={(e) => setContact(e.target.value)}
              >
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="Telephone">Telephone</MenuItem>
                <MenuItem value="Teams">Teams</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id="speedmeeting-state-label">State</InputLabel>
              <Select
                labelId="speedmeeting-state-label"
                id="speedmeeting-state"
                value={speedmeetingStateId}
                label="State"
                onChange={(e) => setSpeedmeetingStateId(e.target.value)}
              >
                {speedmeetingStates.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.state_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Button 
          variant="contained" 
          color="primary" 
          sx={{ mt: 3 }} 
          onClick={handleSubmit}
          disabled={
            !subject || 
            !description || 
            !startDate || 
            !endDate || 
            !contact || 
            !speedmeetingStateId ||
            (endDate && startDate && new Date(endDate) <= new Date(startDate))
          }
        >
          Schedule Speedmeeting
        </Button>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Footer />
    </Box>
  );
}